import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AlertColor } from '@mui/material';

interface Alert {
    title: string;
    message: string;
    severity: AlertColor;
    id: number;
}

interface AlertContextType {
    alerts: Alert[];
    addAlert: (message: string, severity?: AlertColor, title?: string) => void;
    removeAlert: (id: number) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlerts = (): AlertContextType => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlerts must be used within a AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const addAlert = (message: string, severity: AlertColor = "info", title: string = "") => {
        const newAlert = { title, message, severity, id: Date.now() };
        setAlerts(prevAlerts => [...prevAlerts, newAlert]);
    };
    const removeAlert = (id: number) => {
        setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== id));
    };

    return (
        <AlertContext.Provider value={{ addAlert, removeAlert, alerts }}>
            {children}
        </AlertContext.Provider>
    );
};
